import { isAxiosError } from 'axios';
import { CROSS_VALIDATION_ERROR_CODE } from '@/features/validation-gate/constants';

export const IDEMPOTENCY_IN_PROGRESS = 'IDEMPOTENCY_IN_PROGRESS';
export const NETWORK_ERROR_CODE = 'NETWORK';

/**
 * What a failed send means for a queued contribution:
 * - network: no answer (offline, DNS, CORS, timeout). Keep it, stop the flush.
 * - auth: 401, the session is over. Keep it, pause until the person signs in.
 * - in-progress: 409 IDEMPOTENCY_IN_PROGRESS, the first send is still being
 *   processed. Keep it, move on to the next one.
 * - gate: 403 CROSS_VALIDATION_REQUIRED. Keep it, stop: every contribution of
 *   this account would get the same answer until it validates.
 * - retry-later: 408, 425, 429, 5xx. Keep it, stop and try again later.
 * - final: any other 4xx (400, 422...). Refused for good: shown as failed.
 */
export type SendOutcome = 'network' | 'auth' | 'in-progress' | 'gate' | 'retry-later' | 'final';

export type SendFailure = { outcome: SendOutcome; code: string; status?: number };

const SERVER_CODE = /^[A-Z][A-Z0-9_]{1,63}$/;

function serverCode(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  for (const key of ['code', 'message'] as const) {
    const raw = Reflect.get(data, key);
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string' && SERVER_CODE.test(value)) return value;
  }
  return undefined;
}

/** No HTTP answer at all: the device is offline or the server unreachable. A cancelled request is not one. */
export function isNetworkError(error: unknown): boolean {
  return isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED';
}

export function classifySendError(error: unknown): SendFailure {
  if (!isAxiosError(error)) return { outcome: 'retry-later', code: 'UNKNOWN' };
  if (!error.response) return { outcome: 'network', code: NETWORK_ERROR_CODE };

  const status = error.response.status;
  const code = serverCode(error.response.data) ?? `HTTP_${status}`;
  if (status === 401) return { outcome: 'auth', code, status };
  if (status === 409 && code === IDEMPOTENCY_IN_PROGRESS) return { outcome: 'in-progress', code, status };
  if (status === 403 && code === CROSS_VALIDATION_ERROR_CODE) return { outcome: 'gate', code, status };
  if (status === 408 || status === 425 || status === 429 || status >= 500) return { outcome: 'retry-later', code, status };
  return { outcome: 'final', code, status };
}
